/**
 * Auth Context
 *
 * Gestione centralizzata dello stato di autenticazione.
 * Utilizza authService per comunicare con le API (mock o reali).
 * Include gestione automatica del token refresh.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { STORAGE_KEYS, TOKEN_REFRESH_CONFIG } from '@/config/constants';
import { authService, type User, type LoginRequest } from '../api';
import { isDev } from '@/config/env';
import {
  setOnUnauthorizedCallback,
  clearOnUnauthorizedCallback,
} from '@/api/client';

// =============================================================================
// Types
// =============================================================================

interface AuthContextType {
  /** Se l'utente è autenticato */
  isAuthenticated: boolean;
  /** Dati utente (null se non autenticato) */
  user: User | null;
  /** Se è in corso un'operazione di auth */
  isLoading: boolean;
  /** Errore dell'ultima operazione */
  error: string | null;
  /** Effettua login */
  login: (data: LoginRequest) => Promise<boolean>;
  /** Effettua logout */
  logout: () => Promise<void>;
  /** Pulisce l'errore */
  clearError: () => void;
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Ref per l'intervallo di refresh
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref per evitare refresh concorrenti
  const isRefreshingRef = useRef(false);

  /**
   * Salva i token in localStorage per persistenza
   */
  const saveTokens = useCallback(
    (token: string, refreshToken: string, expiresAt: number) => {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt.toString());
    },
    []
  );

  /**
   * Rimuove tutti i token da localStorage
   */
  const removeTokens = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
  }, []);

  /**
   * Recupera i token da localStorage
   */
  const getStoredTokens = useCallback(() => {
    return {
      token: localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN),
      refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
      expiresAt: parseInt(
        localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT) || '0',
        10
      ),
    };
  }, []);

  /**
   * Verifica se il token sta per scadere
   */
  const isTokenExpiringSoon = useCallback(() => {
    const { expiresAt } = getStoredTokens();
    if (!expiresAt) return true;
    return Date.now() >= expiresAt - TOKEN_REFRESH_CONFIG.REFRESH_THRESHOLD;
  }, [getStoredTokens]);

  /**
   * Effettua il refresh dei token
   */
  const performTokenRefresh = useCallback(async (): Promise<boolean> => {
    // Evita refresh concorrenti
    if (isRefreshingRef.current) {
      if (isDev) console.log('[Auth] Refresh already in progress, skipping');
      return false;
    }

    const { refreshToken } = getStoredTokens();
    if (!refreshToken) {
      if (isDev) console.log('[Auth] No refresh token available');
      return false;
    }

    isRefreshingRef.current = true;

    try {
      if (isDev) console.log('[Auth] Refreshing tokens...');
      const response = await authService.refreshToken(refreshToken);

      // Salva i nuovi token
      saveTokens(response.token, response.refreshToken, response.expiresAt);

      if (isDev) console.log('[Auth] Tokens refreshed successfully');
      return true;
    } catch (err) {
      if (isDev) console.error('[Auth] Token refresh failed:', err);
      // Se il refresh fallisce, effettua logout
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [getStoredTokens, saveTokens]);

  /**
   * Avvia il timer per il refresh automatico
   */
  const startRefreshTimer = useCallback(() => {
    // Pulisci eventuali timer esistenti
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Imposta il nuovo timer
    refreshIntervalRef.current = setInterval(async () => {
      if (isTokenExpiringSoon()) {
        const success = await performTokenRefresh();
        if (!success) {
          // Se il refresh fallisce, effettua logout silenzioso
          if (isDev) console.log('[Auth] Auto-logout due to refresh failure');
          removeTokens();
          setUser(null);
          setIsAuthenticated(false);
          navigate('/login');
        }
      }
    }, TOKEN_REFRESH_CONFIG.REFRESH_CHECK_INTERVAL);

    if (isDev) console.log('[Auth] Refresh timer started');
  }, [isTokenExpiringSoon, performTokenRefresh, removeTokens, navigate]);

  /**
   * Ferma il timer di refresh
   */
  const stopRefreshTimer = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
      if (isDev) console.log('[Auth] Refresh timer stopped');
    }
  }, []);

  /**
   * Verifica la sessione esistente al mount
   */
  useEffect(() => {
    const validateSession = async () => {
      const { token, refreshToken } = getStoredTokens();

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        // Se il token sta per scadere e abbiamo un refresh token, rinnova prima
        if (isTokenExpiringSoon() && refreshToken) {
          const refreshed = await performTokenRefresh();
          if (!refreshed) {
            throw new Error('Token refresh failed');
          }
        }

        const userData = await authService.validateToken(token);
        setUser(userData);
        setIsAuthenticated(true);

        // Avvia il timer di refresh automatico
        startRefreshTimer();
      } catch {
        // Token non valido o scaduto
        removeTokens();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateSession();

    // Cleanup: ferma il timer quando il componente viene smontato
    return () => {
      stopRefreshTimer();
    };
  }, [
    getStoredTokens,
    removeTokens,
    isTokenExpiringSoon,
    performTokenRefresh,
    startRefreshTimer,
    stopRefreshTimer,
  ]);

  /**
   * Registra callback per gestione 401 Unauthorized globale.
   * Quando l'httpClient riceve un 401, invoca questo callback per fare logout.
   */
  useEffect(() => {
    const handleUnauthorized = () => {
      if (isDev) console.log('[Auth] Received 401 - forcing logout');

      // Ferma il timer di refresh
      stopRefreshTimer();

      // Pulisci stato (i token sono già stati rimossi dall'httpClient)
      setUser(null);
      setIsAuthenticated(false);
      setError(null);

      // Redirect a login
      navigate('/login');
    };

    // Registra il callback
    setOnUnauthorizedCallback(handleUnauthorized);

    // Cleanup: rimuovi callback al unmount
    return () => {
      clearOnUnauthorizedCallback();
    };
  }, [stopRefreshTimer, navigate]);

  /**
   * Effettua login
   */
  const login = useCallback(
    async (data: LoginRequest): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await authService.login(data);

        // Salva tutti i token
        saveTokens(response.token, response.refreshToken, response.expiresAt);

        // Aggiorna stato
        setUser(response.user);
        setIsAuthenticated(true);

        // Avvia il timer di refresh automatico
        startRefreshTimer();

        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Errore durante il login';
        setError(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [saveTokens, startRefreshTimer]
  );

  /**
   * Effettua logout
   */
  const logout = useCallback(async () => {
    setIsLoading(true);

    // Ferma il timer di refresh
    stopRefreshTimer();

    try {
      await authService.logout();
    } catch {
      // Ignora errori di logout (procedi comunque)
    } finally {
      // Pulisci stato locale e tutti i token
      removeTokens();
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setIsLoading(false);

      // Redirect a login
      navigate('/login');
    }
  }, [removeTokens, stopRefreshTimer, navigate]);

  /**
   * Pulisce l'errore
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // =============================================================================
  // Render
  // =============================================================================

  // Memoizza il context value per evitare re-render non necessari
  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      user,
      isLoading,
      error,
      login,
      logout,
      clearError,
    }),
    [isAuthenticated, user, isLoading, error, login, logout, clearError]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook per accedere al contesto di autenticazione
 *
 * @example
 * const { user, login, logout, isAuthenticated } = useAuth();
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
